import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import classNames from '../../../utils/classNames';
import './index.scss';

const resolveBackdropRoot = () => (typeof document === 'undefined' ? null : document.body);

/**
 * Full-page blurred scrim shown behind an open filter panel, plus a crisp,
 * interactive copy of the filter toggle pinned on top of the scrim.
 *
 * The scrim renders in a body-level portal so its blur escapes any ancestor
 * that establishes a containing block for `position: fixed` (panels live inside
 * surfaces that set `backdrop-filter` / `container-type`, which would otherwise
 * confine the scrim to a washed-out box instead of the full page). That same
 * containment means the in-flow toggle can't be lifted above the body-level
 * scrim with z-index alone, so callers hide their in-flow toggle while open and
 * pass a crisp copy as `children`; we position it over the scrim, anchored to
 * the in-flow toggle's box (tracked via `anchorRef`).
 */
export default function FilterScrimBackdrop({
  open,
  anchorRef,
  onClose,
  children,
  className = '',
  floatingClassName = '',
}) {
  const backdropRoot = React.useMemo(() => resolveBackdropRoot(), []);
  const [anchorRect, setAnchorRect] = React.useState(null);

  React.useLayoutEffect(() => {
    if (!open) {
      setAnchorRect(null);
      return undefined;
    }

    const measure = () => {
      const node = anchorRef?.current;
      if (node) {
        setAnchorRect(node.getBoundingClientRect());
      }
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, anchorRef]);

  if (!backdropRoot) {
    return null;
  }

  return createPortal(
    <>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key="filter-scrim-backdrop"
            className={classNames('filter-scrim-backdrop', className)}
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            onPointerDown={onClose}
          />
        ) : null}
      </AnimatePresence>
      {open && anchorRect ? (
        <div
          className={classNames('filter-scrim-backdrop__floating', floatingClassName)}
          style={{
            top: `${anchorRect.top}px`,
            left: `${anchorRect.left}px`,
            minWidth: `${anchorRect.width}px`,
            height: `${anchorRect.height}px`,
          }}
        >
          {children}
        </div>
      ) : null}
    </>,
    backdropRoot,
  );
}

FilterScrimBackdrop.propTypes = {
  open: PropTypes.bool,
  anchorRef: PropTypes.shape({ current: PropTypes.any }),
  onClose: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
  floatingClassName: PropTypes.string,
};
